import React, { useState } from 'react';
import { legalAPI } from '../utils/api';
import { X, ShieldAlert, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// ─── Config ───────────────────────────────────────────────────────────────────

const ESCALATION_TYPES = [
  { value: 'client_complaint',         label: 'Client Complaint',              icon: '😠', color: 'text-orange-600' },
  { value: 'document_dispute',         label: 'Document Dispute',              icon: '📄', color: 'text-blue-600' },
  { value: 'visa_rejection_dispute',   label: 'Visa Rejection Dispute',        icon: '🚫', color: 'text-red-600' },
  { value: 'fraud_concern',            label: 'Fraud Concern',                 icon: '🔍', color: 'text-purple-600' },
  { value: 'legal_threat',             label: 'Legal Threat Received',         icon: '⚖️', color: 'text-red-700' },
  { value: 'regulatory_compliance',    label: 'Regulatory / Compliance Issue', icon: '📋', color: 'text-indigo-600' },
  { value: 'fee_refund_dispute',       label: 'Fee / Refund Dispute',          icon: '💰', color: 'text-yellow-700' },
  { value: 'misrepresentation_claim',  label: 'Misrepresentation Claim',       icon: '📢', color: 'text-rose-600' },
  { value: 'privacy_data_concern',     label: 'Privacy / Data Concern',        icon: '🔒', color: 'text-slate-600' },
  { value: 'other',                    label: 'Other',                         icon: '📌', color: 'text-slate-500' },
];

const URGENCY_LEVELS = [
  { value: 'low',      label: 'Low',      desc: 'No immediate risk, can be addressed within 2 weeks', color: 'border-slate-300 bg-slate-50', active: 'border-slate-600 bg-slate-100', badge: 'bg-slate-100 text-slate-700' },
  { value: 'medium',   label: 'Medium',   desc: 'Needs attention within 1 week',                       color: 'border-blue-200 bg-blue-50',   active: 'border-blue-600 bg-blue-100',   badge: 'bg-blue-100 text-blue-700' },
  { value: 'high',     label: 'High',     desc: 'Urgent — requires legal review within 48 hours',      color: 'border-amber-300 bg-amber-50', active: 'border-amber-600 bg-amber-100', badge: 'bg-amber-100 text-amber-700' },
  { value: 'critical', label: 'Critical', desc: 'Immediate legal action required — potential lawsuit',  color: 'border-red-300 bg-red-50',     active: 'border-red-600 bg-red-100',     badge: 'bg-red-100 text-red-700' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function EscalationModal({
  onClose,
  onSuccess,
  // Pre-fill context — all optional
  caseId        = null,
  clientId      = null,
  clientName    = '',
  ucin          = '',
  visaType      = '',
  caseOfficer   = '',
  documents     = [],  // string[] of document names for selection
  completeFileAvailable = false,
}) {
  const [step, setStep]           = useState(1); // 1: details, 2: attorney brief, 3: confirm
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    escalation_type:            '',
    urgency:                    '',
    incident_date:              '',
    summary:                    '',
    detailed_description:       '',
    client_demands:             '',
    prior_resolution_attempts:  '',
    potential_liability_inr:    '',
    witnesses:                  '',
    supporting_document_names:  [],
    complete_file_attached:     false,
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleDoc = (name) => set('supporting_document_names',
    form.supporting_document_names.includes(name)
      ? form.supporting_document_names.filter(d => d !== name)
      : [...form.supporting_document_names, name]
  );

  const canProceedStep1 = form.escalation_type && form.urgency && form.summary.trim().length >= 10;
  const canProceedStep2 = form.detailed_description.trim().length >= 30;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await legalAPI.createEscalation({
        case_id:                    caseId,
        client_id:                  clientId,
        client_name:                clientName || 'Unknown',
        ucin:                       ucin || null,
        visa_type:                  visaType || null,
        case_officer_name:          caseOfficer || null,
        escalation_type:            form.escalation_type,
        urgency:                    form.urgency,
        summary:                    form.summary,
        detailed_description:       form.detailed_description,
        incident_date:              form.incident_date || null,
        client_demands:             form.client_demands || null,
        prior_resolution_attempts:  form.prior_resolution_attempts || null,
        potential_liability_inr:    form.potential_liability_inr ? parseFloat(form.potential_liability_inr) : null,
        witnesses:                  form.witnesses || null,
        supporting_document_names:  form.supporting_document_names,
        complete_file_attached:     form.complete_file_attached,
      });
      toast.success('Escalation raised. Legal team has been notified.');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to raise escalation');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedType = ESCALATION_TYPES.find(t => t.value === form.escalation_type);
  const selectedUrgency = URGENCY_LEVELS.find(u => u.value === form.urgency);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="bg-gradient-to-r from-red-700 to-rose-600 px-6 py-5 text-white shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <ShieldAlert size={26} className="text-white/80" />
              <div>
                <p className="font-bold text-xl">Raise Legal Escalation</p>
                {clientName && <p className="text-red-200 text-sm mt-0.5">{clientName}{ucin ? ` · ${ucin}` : ''}</p>}
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg"><X size={20} /></button>
          </div>
          {/* Step indicator */}
          <div className="flex items-center gap-2 mt-4">
            {[1, 2, 3].map(s => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${step > s ? 'bg-white text-red-700 border-white' : step === s ? 'bg-white/20 text-white border-white' : 'bg-transparent text-red-200 border-red-400'}`}>
                  {step > s ? '✓' : s}
                </div>
                <span className={`text-xs ${step === s ? 'text-white font-semibold' : 'text-red-200'}`}>
                  {s === 1 ? 'Escalation Type' : s === 2 ? 'Attorney Brief' : 'Review & Submit'}
                </span>
                {s < 3 && <div className="w-6 h-px bg-red-400 mx-1" />}
              </div>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* ── Step 1: Type + Urgency + Summary ── */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-bold text-slate-800 mb-2">Escalation Type *</label>
                <div className="grid grid-cols-2 gap-2">
                  {ESCALATION_TYPES.map(t => (
                    <label key={t.value} className={`flex items-center gap-2.5 p-3 rounded-xl border-2 cursor-pointer transition-all ${form.escalation_type === t.value ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:border-slate-300'}`}>
                      <input type="radio" name="type" value={t.value} checked={form.escalation_type === t.value} onChange={e => set('escalation_type', e.target.value)} className="sr-only" />
                      <span className="text-lg">{t.icon}</span>
                      <span className={`text-sm font-semibold ${form.escalation_type === t.value ? 'text-red-700' : 'text-slate-700'}`}>{t.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-800 mb-2">Urgency Level *</label>
                <div className="grid grid-cols-2 gap-2">
                  {URGENCY_LEVELS.map(u => (
                    <label key={u.value} className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${form.urgency === u.value ? u.active : u.color}`}>
                      <input type="radio" name="urgency" value={u.value} checked={form.urgency === u.value} onChange={e => set('urgency', e.target.value)} className="sr-only" />
                      <p className="font-bold text-sm text-slate-800">{u.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{u.desc}</p>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-bold text-slate-800 mb-1">Summary * <span className="text-slate-400 font-normal">({form.summary.length}/150 chars)</span></label>
                  <input type="text" maxLength={150} value={form.summary} onChange={e => set('summary', e.target.value)}
                    placeholder="One-line description of the escalation issue"
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-red-500 outline-none" />
                  {form.summary.length > 0 && form.summary.length < 10 && (
                    <p className="text-xs text-red-500 mt-1">At least 10 characters required</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-800 mb-1">Incident Date</label>
                  <input type="date" value={form.incident_date} onChange={e => set('incident_date', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-red-500 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-800 mb-1">Estimated Liability (INR)</label>
                  <input type="number" value={form.potential_liability_inr} onChange={e => set('potential_liability_inr', e.target.value)}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-red-500 outline-none" />
                </div>
              </div>
            </div>
          )}

          {/* ── Step 2: Attorney Brief ── */}
          {step === 2 && (
            <div className="space-y-5">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex gap-3">
                <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">Everything in this section is <strong>privileged and confidential</strong>. It will only be visible to legal counsel, admins, and super admins.</p>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-800 mb-1">Detailed Description for Attorney * <span className="text-slate-400 font-normal">(chronological account of events)</span></label>
                <textarea rows={5} value={form.detailed_description} onChange={e => set('detailed_description', e.target.value)}
                  placeholder="Describe the full sequence of events, all communications, what was promised vs. what happened, relevant dates, and any other details the attorney needs to understand the situation…"
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-red-500 outline-none resize-none" />
                {form.detailed_description.length > 0 && form.detailed_description.length < 30 && (
                  <p className="text-xs text-red-500 mt-1">At least 30 characters required</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-800 mb-1">Client's Demands or Claims</label>
                <textarea rows={2} value={form.client_demands} onChange={e => set('client_demands', e.target.value)}
                  placeholder="What is the client demanding? Refund, compensation, apology, legal action?"
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-red-500 outline-none resize-none" />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-800 mb-1">Prior Resolution Attempts</label>
                <textarea rows={2} value={form.prior_resolution_attempts} onChange={e => set('prior_resolution_attempts', e.target.value)}
                  placeholder="What was already attempted to resolve this? Calls, emails, refund offers, etc."
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-red-500 outline-none resize-none" />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-800 mb-1">Witnesses <span className="text-slate-400 font-normal">(optional)</span></label>
                <input type="text" value={form.witnesses} onChange={e => set('witnesses', e.target.value)}
                  placeholder="Names of any witnesses or involved staff members"
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-red-500 outline-none" />
              </div>

              {/* Document selection */}
              {documents.length > 0 && (
                <div>
                  <label className="block text-sm font-bold text-slate-800 mb-2">Relevant Documents</label>
                  <div className="grid grid-cols-2 gap-1.5 max-h-36 overflow-y-auto pr-1">
                    {documents.map(doc => (
                      <label key={doc} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-xs transition-all ${form.supporting_document_names.includes(doc) ? 'border-red-400 bg-red-50 text-red-800' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                        <input type="checkbox" checked={form.supporting_document_names.includes(doc)} onChange={() => toggleDoc(doc)} className="w-3 h-3 text-red-500" />
                        <span className="truncate font-medium">{doc}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Complete file */}
              {completeFileAvailable && (
                <label className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${form.complete_file_attached ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input type="checkbox" checked={form.complete_file_attached} onChange={e => set('complete_file_attached', e.target.checked)} className="mt-0.5 w-4 h-4 text-red-500" />
                  <div>
                    <p className="text-sm font-bold text-slate-800">Attach Complete File Approval Record</p>
                    <p className="text-xs text-slate-500 mt-0.5">Include the legally signed client approval certificate as evidence</p>
                  </div>
                </label>
              )}
            </div>
          )}

          {/* ── Step 3: Confirm ── */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="p-5 bg-red-50 border-2 border-red-200 rounded-2xl space-y-3">
                <p className="font-bold text-red-800 text-lg">Review Before Submitting</p>

                <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  <div><span className="text-slate-500 text-xs">Client</span><p className="font-semibold">{clientName || '—'}{ucin ? ` · ${ucin}` : ''}</p></div>
                  <div><span className="text-slate-500 text-xs">Type</span><p className="font-semibold">{selectedType?.icon} {selectedType?.label}</p></div>
                  <div><span className="text-slate-500 text-xs">Urgency</span>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold mt-0.5 ${selectedUrgency?.badge}`}>{selectedUrgency?.label}</span>
                  </div>
                  {form.incident_date && <div><span className="text-slate-500 text-xs">Incident Date</span><p className="font-semibold">{form.incident_date}</p></div>}
                  {form.potential_liability_inr && <div><span className="text-slate-500 text-xs">Est. Liability</span><p className="font-semibold text-red-700">INR {Number(form.potential_liability_inr).toLocaleString('en-IN')}</p></div>}
                </div>

                <div><span className="text-slate-500 text-xs">Summary</span><p className="font-semibold text-sm">{form.summary}</p></div>
                <div><span className="text-slate-500 text-xs">Description ({form.detailed_description.length} chars)</span><p className="text-sm text-slate-700 line-clamp-3">{form.detailed_description}</p></div>

                {form.supporting_document_names.length > 0 && (
                  <div><span className="text-slate-500 text-xs">Attached Documents ({form.supporting_document_names.length})</span>
                    <p className="text-sm text-slate-700">{form.supporting_document_names.join(', ')}</p>
                  </div>
                )}
                {form.complete_file_attached && (
                  <div className="flex items-center gap-2 text-sm text-green-700 font-medium">✓ Complete File approval record will be attached</div>
                )}
              </div>

              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600">
                <strong>Important:</strong> Once submitted, this escalation will immediately appear on the Legal Counsel Dashboard and all legal team members will be notified. This action is logged and cannot be undone.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-4 flex items-center justify-between gap-3 bg-slate-50 shrink-0">
          <button onClick={step > 1 ? () => setStep(s => s - 1) : onClose}
            className="px-5 py-2.5 border border-slate-300 text-slate-700 rounded-xl font-medium hover:bg-slate-100 text-sm">
            {step > 1 ? '← Back' : 'Cancel'}
          </button>

          {step < 3 && (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={step === 1 ? !canProceedStep1 : !canProceedStep2}
              className="px-6 py-2.5 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
            >
              Continue →
            </button>
          )}

          {step === 3 && (
            <button onClick={handleSubmit} disabled={submitting}
              className="flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 disabled:opacity-50 text-sm">
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : <><ShieldAlert size={16} /> Submit Escalation</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
